import { Middleware } from '@reduxjs/toolkit';
import { saveRecipesToStorage, STORAGE_KEYS } from '../../utils/localStorage';
import { addClient } from '../slices/clientsSlice';
import {
  recalculateOrderFulfillment,
  updateOrderStatus,
  copyOrderToCompleted,
  updateOrder,
  markOrderInventoryDeducted
} from '../slices/ordersSlice';
import { deductInventoryForOrder } from '../slices/todoSlice';
import { v4 as uuidv4 } from 'uuid';

// Debounce helper
let saveTimeout: NodeJS.Timeout | null = null;
const DEBOUNCE_DELAY = 300;
const ORDERS_STORAGE_KEY = 'orders';
const COMPLETED_ORDERS_STORAGE_KEY = 'completed-orders';
const CALENDAR_EVENTS_STORAGE_KEY = 'calendar-events';
const TODO_TASKS_STORAGE_KEY = 'todo-tasks';
const COMPLETED_BATCHES_STORAGE_KEY = 'completed-batches';
const COMPLETED_PRODUCTS_STORAGE_KEY = 'completed-products';
const RAW_INGREDIENTS_STORAGE_KEY = 'raw-ingredients';
const SUPPLIES_STORAGE_KEY = 'supplies';
const PRICE_PER_DOZEN_STORAGE_KEY = 'price-per-dozen';
const RETAILER_PRICE_UNDER_50_STORAGE_KEY = 'retailer-price-under-50';
const RETAILER_PRICE_50_PLUS_STORAGE_KEY = 'retailer-price-50-plus';
const PRICING_DEALS_STORAGE_KEY = 'pricing-deals';
const PRICING_DELIVERY_ZONES_STORAGE_KEY = 'pricing-delivery-zones';
const PRICING_DELIVERY_RANGES_STORAGE_KEY = 'pricing-delivery-ranges';
const REVIEW_QUEUE_STORAGE_KEY = 'review-queue';

/**
 * Middleware to sync Redux state with localStorage
 * Debounces saves to avoid excessive writes
 * Also handles auto-generation of tasks when orders are finalized
 */
export const localStorageMiddleware: Middleware = store => next => action => {
  // Get state before the action
  const prevState = store.getState();

  const result = next(action);

  // Type guard to check if action has type and payload properties
  const hasType = (act: unknown): act is { type: string } => {
    return typeof act === 'object' && act !== null && 'type' in act && typeof (act as any).type === 'string';
  };

  const hasPayload = (act: unknown): act is { payload: any } => {
    return typeof act === 'object' && act !== null && 'payload' in act;
  };

  // Helper function to create a client from an order
  const createClientFromOrder = (order: any, state: any) => {
    // If order already has a clientId, it's linked to an existing client
    // Don't create a new client in this case
    if (order.clientId) {
      console.log(`[ClientCreation] Order already linked to client ${order.clientId}, skipping client creation`);
      return;
    }

    const clients = state.clients?.clients || [];
    const customerEmail = order.email?.trim().toLowerCase();
    const customerPhone = order.phone?.trim();

    // Check if client already exists by email or phone
    const existingClient = clients.find((client: any) => {
      const clientEmail = client.email?.trim().toLowerCase();
      const clientPhone = client.phone?.trim();

      // Match by email (if both have email)
      if (customerEmail && clientEmail && customerEmail === clientEmail) {
        return true;
      }

      // Match by phone (if both have phone)
      if (customerPhone && clientPhone && customerPhone === clientPhone) {
        return true;
      }

      return false;
    });

    // If no existing client found and customer name exists, create a new client
    if (!existingClient && order.customerName?.trim()) {
      const newClient = {
        id: uuidv4(),
        type: 'individual' as const,
        name: order.customerName,
        email: order.email || '',
        phone: order.phone || '',
        address: [order.streetAddress, order.city, order.province, order.postalCode].filter(Boolean).join(', '),
        notes: `Auto-created from order on ${new Date().toLocaleDateString()}`,
        preferences: '',
        createdAt: new Date().toISOString()
      };

      store.dispatch(addClient(newClient) as any);

      // Update the order with the new client ID
      store.dispatch(
        updateOrder({
          ...order,
          clientId: newClient.id
        })
      );

      console.log(`[ClientCreation] Created new client: ${newClient.name} and linked to order`);
    } else if (existingClient) {
      // Update the order with the existing client ID
      store.dispatch(
        updateOrder({
          ...order,
          clientId: existingClient.id
        })
      );

      console.log(`[ClientCreation] Client already exists: ${order.customerName}, linked to order`);
    }
  };

  // Check if an order was added with isFinalized=true (batch generation case)
  if (hasType(action) && action.type === 'orders/addOrder' && hasPayload(action) && action.payload) {
    const newState = store.getState();
    const addedOrder = action.payload;

    // If the order is already finalized when added, create a client
    if (addedOrder.isFinalized) {
      createClientFromOrder(addedOrder, newState);
    }
  }

  // Check if an order was just finalized (manual finalization case)
  if (hasType(action) && action.type === 'orders/updateOrder' && hasPayload(action) && action.payload) {
    const newState = store.getState();
    const updatedOrder = action.payload;

    // Find the previous version of this order
    const prevOrder = prevState.orders?.orders?.find((o: any) => o.id === updatedOrder.id);

    // If the order was just finalized (changed from false to true)
    if (prevOrder && !prevOrder.isFinalized && updatedOrder.isFinalized) {
      // AUTOMATIC TASK GENERATION DISABLED
      // Tasks should be created manually through the task creation interface

      // Create client from finalized order
      createClientFromOrder(updatedOrder, newState);
    }
  }

  // Check if completed products changed (inventory change)
  if (hasType(action) && action.type.startsWith('todo/')) {
    const newState = store.getState();
    const prevCompletedProducts = prevState.todo?.completedProducts || [];
    const newCompletedProducts = newState.todo?.completedProducts || [];

    // If completed products changed, recalculate order fulfillment
    if (
      prevCompletedProducts.length !== newCompletedProducts.length ||
      JSON.stringify(prevCompletedProducts) !== JSON.stringify(newCompletedProducts)
    ) {
      store.dispatch(recalculateOrderFulfillment(newCompletedProducts));
      console.log('[OrderFulfillment] Recalculated order fulfillment after inventory change');
    }
  }

  // Check if a calendar event completion was toggled (for delivery tasks)
  if (hasType(action) && action.type === 'calendar/toggleEventCompletion' && hasPayload(action)) {
    const newState = store.getState();
    const eventId = action.payload;
    const event = newState.calendar?.events?.find((e: any) => e.id === eventId);

    // Enhanced debugging with grouped console logs
    console.group('[🔄 Event Completion Toggle]');
    console.log('Event ID:', eventId);
    console.log('Event Title:', event?.title);
    console.log('Has orderId:', !!event?.orderId);
    console.log('Order ID:', event?.orderId || 'N/A');
    console.log('Completed:', event?.completed);

    // If the event has an associated orderId, update the order status
    if (event && event.orderId) {
      const order = newState.orders?.orders?.find((o: any) => o.id === event.orderId);
      console.log('Found Order:', order?.customerName || 'NOT FOUND');
      console.log('Current Order Status:', order?.status);

      if (order) {
        const newStatus = event.completed ? 'Completed' : 'In Progress';
        console.log('→ Dispatching updateOrderStatus:', newStatus);
        store.dispatch(updateOrderStatus({ id: order.id, status: newStatus }));

        // Copy order to completed orders when delivery is marked complete
        if (event.completed) {
          console.log('→ Dispatching copyOrderToCompleted');
          store.dispatch(copyOrderToCompleted(event.orderId));

          // Log the completedOrders array size after dispatch
          const updatedState = store.getState();
          console.log('✓ CompletedOrders array size:', updatedState.orders.completedOrders.length);

          // Deduct inventory if not already deducted
          if (!order.inventoryDeducted && order.items && order.items.length > 0) {
            const items = order.items.map((item: any) => ({
              recipeName: item.recipeName,
              quantity: item.quantity
            }));
            store.dispatch(deductInventoryForOrder({ orderId: order.id, items }));
            store.dispatch(markOrderInventoryDeducted(order.id));
            console.log('✓ Inventory deducted for order');
          } else if (order.inventoryDeducted) {
            console.log('ℹ Inventory already deducted - skipping');
          }
        }
      }
    } else {
      console.warn('⚠️  Event has no orderId - middleware will not process completion');
    }
    console.groupEnd();
  }

  // Check if a delivery task was marked complete (copy associated order to completed orders)
  if (hasType(action) && action.type === 'todo/updateTaskStatus' && hasPayload(action)) {
    const newState = store.getState();
    const { id, status } = action.payload;

    // If task is marked as completed
    if (status === 'completed') {
      const task = newState.todo?.tasks?.find((t: any) => t.id === id);

      // If task has associated scheduledOrderIds, copy the orders to completed orders
      if (task && task.scheduledOrderIds && task.scheduledOrderIds.length > 0) {
        task.scheduledOrderIds.forEach((orderId: string) => {
          const order = newState.orders?.orders?.find((o: any) => o.id === orderId);

          // Copy order to completed
          store.dispatch(copyOrderToCompleted(orderId));
          console.log(`[DeliveryCompletion] Copied order ${orderId} to completed orders based on task completion`);

          // Deduct inventory if not already deducted
          if (order && !order.inventoryDeducted && order.items && order.items.length > 0) {
            const items = order.items.map((item: any) => ({
              recipeName: item.recipeName,
              quantity: item.quantity
            }));
            store.dispatch(deductInventoryForOrder({ orderId: order.id, items }));
            store.dispatch(markOrderInventoryDeducted(order.id));
            console.log(`[InventoryDeduction] Deducted inventory for completed order ${order.customerName || orderId}`);
          } else if (order?.inventoryDeducted) {
            console.log(`[InventoryDeduction] Skipping inventory deduction for order ${orderId} - already deducted`);
          }
        });
      }
    }
  }

  // Sync on recipe-related, order-related, calendar-related, todo-related, pricing-related, or review queue actions
  if (hasType(action)) {
    const isRecipeAction = action.type.startsWith('recipes/');
    const isOrderAction = action.type.startsWith('orders/');
    const isCalendarAction = action.type.startsWith('calendar/');
    const isTodoAction = action.type.startsWith('todo/');
    const isPricingAction = action.type.startsWith('pricing/');
    const isReviewQueueAction = action.type.startsWith('reviewQueue/');

    if (
      isRecipeAction ||
      isOrderAction ||
      isCalendarAction ||
      isTodoAction ||
      isPricingAction ||
      isReviewQueueAction
    ) {
      // Clear existing timeout
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }

      // Debounce the save
      saveTimeout = setTimeout(() => {
        const state = store.getState();

        // Save recipes
        if (isRecipeAction && state.recipes) {
          // Fillings are now stored in PostgreSQL, not localStorage
          // Components are now stored in PostgreSQL, not localStorage
        }

        // Save orders
        if (isOrderAction && state.orders) {
          try {
            localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(state.orders.orders));
            localStorage.setItem(COMPLETED_ORDERS_STORAGE_KEY, JSON.stringify(state.orders.completedOrders));
          } catch (error) {
            console.error('Failed to save orders:', error);
          }
        }

        // Save calendar events
        if (isCalendarAction && state.calendar) {
          try {
            localStorage.setItem(CALENDAR_EVENTS_STORAGE_KEY, JSON.stringify(state.calendar.events));
          } catch (error) {
            console.error('Failed to save calendar events:', error);
          }
        }

        // Save todo tasks, completed batches, completed products, raw ingredients, and supplies
        if (isTodoAction && state.todo) {
          try {
            localStorage.setItem(TODO_TASKS_STORAGE_KEY, JSON.stringify(state.todo.tasks));
            localStorage.setItem(COMPLETED_BATCHES_STORAGE_KEY, JSON.stringify(state.todo.completedBatches));
            localStorage.setItem(COMPLETED_PRODUCTS_STORAGE_KEY, JSON.stringify(state.todo.completedProducts));
            localStorage.setItem(RAW_INGREDIENTS_STORAGE_KEY, JSON.stringify(state.todo.rawIngredients));
            localStorage.setItem(SUPPLIES_STORAGE_KEY, JSON.stringify(state.todo.supplies));
          } catch (error) {
            console.error('Failed to save todo data:', error);
          }
        }

        // Save pricing data
        if (isPricingAction && state.pricing) {
          try {
            localStorage.setItem(PRICE_PER_DOZEN_STORAGE_KEY, state.pricing.pricePerDozen.toString());
            localStorage.setItem(RETAILER_PRICE_UNDER_50_STORAGE_KEY, state.pricing.retailerPriceUnder50.toString());
            localStorage.setItem(RETAILER_PRICE_50_PLUS_STORAGE_KEY, state.pricing.retailerPrice50Plus.toString());
            localStorage.setItem(PRICING_DEALS_STORAGE_KEY, JSON.stringify(state.pricing.deals));
            localStorage.setItem(PRICING_DELIVERY_ZONES_STORAGE_KEY, JSON.stringify(state.pricing.deliveryZones));
            localStorage.setItem(PRICING_DELIVERY_RANGES_STORAGE_KEY, JSON.stringify(state.pricing.deliveryRanges));
          } catch (error) {
            console.error('Failed to save pricing data:', error);
          }
        }

        // Save review queue
        if (isReviewQueueAction && state.reviewQueue) {
          try {
            localStorage.setItem(REVIEW_QUEUE_STORAGE_KEY, JSON.stringify(state.reviewQueue.items));
          } catch (error) {
            console.error('Failed to save review queue:', error);
          }
        }

        // Batch records are now stored in PostgreSQL, not localStorage
      }, DEBOUNCE_DELAY);
    }
  }

  return result;
};
